import React, { useEffect, useState } from "react";
import {
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Typography,
  Divider,
  Select,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import DatePickerNewLedger from "../../assets/DatePickerNewLedger.svg";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import {
  ButtonContainer,
  DeleteButton,
  labelTypography,
  SaveButton,
  StyledDialog,
  typographyStyles,
} from "./LedgerModal.styles";
import { updateLedger, deleteLedger } from "../../api/ledgerAPI";
import { fetchOrderIds } from "../../api/orderFinalAPI";

interface Entry {
  id: string | undefined;
  date: string;
  orderId: string;
  creditAmount: string;
}
interface AddNewEntryModalProps {
  open: boolean;
  onClose: () => void;
  entry?: Entry | null;
  fetchLedgerData: (page: string, limit: string) => Promise<void>;
  customerId: string;
}

export const LedgerModal: React.FC<AddNewEntryModalProps> = ({
  open,
  onClose,
  entry,
  fetchLedgerData,
  customerId,
}) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(
    entry ? moment(entry.date) : null
  );
  const [orderIds, setOrderIds] = useState<string[]>([]);
  const [orderId, setOrderId] = useState(entry?.orderId || "");
  const [creditAmount, setCreditAmount] = useState(entry?.creditAmount || "");

  useEffect(() => {
    if (!entry && customerId) {
      fetchOrderIds(customerId).then(setOrderIds);
    }
  }, [customerId, entry]);
  const resetForm = () => {
    setSelectedDate(null);
    setOrderId("");
    setCreditAmount("");
  };
  useEffect(() => {
    if (!entry) {
      resetForm();
    } else {
      const parsedDate = moment(entry.date, "YYYY-MM-DD");
      setSelectedDate(parsedDate);
      setOrderId(entry.orderId);
      setCreditAmount(entry.creditAmount);
    }
  }, [entry]);

  const handleSave = async () => {
    if (selectedDate && creditAmount) {
      const payload = {
        id: entry ? entry.id : undefined,
        customerId,
        createdAt: selectedDate.format("YYYY-MM-DD"),
        orderId,
        credit: creditAmount,
      };

      try {
        await updateLedger(payload);
        resetForm();

        onClose();
        fetchLedgerData("1", "10");
      } catch (error) {
        console.error("Error saving ledger entry:", error);
      }
    }
  };

  const handleDelete = async () => {
    if (entry?.id) {
      try {
        await deleteLedger(entry.id);
        onClose();
        fetchLedgerData("1", "10");
      } catch (error) {
        console.error("Error deleting ledger entry:", error);
      }
    }
  };
  const handleClose = () => {
    resetForm();
    onClose();
  };
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <StyledDialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
      >
        <DialogTitle
          sx={{
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={typographyStyles}>
            {entry ? "Edit Entry" : "Add New Entry"}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Choose Date */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" sx={labelTypography}>
                Choose Date
              </Typography>
              <MuiDatePicker
                value={selectedDate}
                onChange={(newDate) => setSelectedDate(newDate)}
                format="DD/MM/YYYY"
                maxDate={moment()}
                disabled={!!entry}
                open={isDatePickerOpen}
                onOpen={() => !entry && setIsDatePickerOpen(true)}
                onClose={() => setIsDatePickerOpen(false)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "Select date",
                    InputProps: {
                      endAdornment: (
                        <IconButton
                          onClick={() => !entry && setIsDatePickerOpen(true)}
                          disabled={!!entry}
                        >
                          <img
                            src={DatePickerNewLedger}
                            alt="Calendar Icon"
                            width={24}
                            height={24}
                            style={{ opacity: entry ? 0.5 : 1 }}
                          />
                        </IconButton>
                      ),
                    },
                  },
                }}
              />
            </Grid>

            {/* Order ID */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" sx={labelTypography}>
                Order ID
              </Typography>
              <Select
                data-testid="OrderID Field"
                fullWidth
                value={orderId}
                disabled={!!entry}
                onChange={(e) => setOrderId(e.target.value as string)}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span style={{ color: "#9CA3AF" }}>Select order ID</span>
                    );
                  }
                  return selected;
                }}
              >
                <MenuItem value="" disabled>
                  Select order ID
                </MenuItem>
                {orderIds.map((id) => (
                  <MenuItem key={id} value={id}>
                    {id}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* Credit Amount */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  fontFamily: "Roboto",
                  paddingBottom: "8px",
                  paddingTop: "20px",
                }}
              >
                Credit Amount
              </Typography>
              <TextField
                data-testid="Credit Field"
                fullWidth
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ marginTop: "80px", border: "1px solid #0000004D" }} />

          {/* Buttons */}
          <ButtonContainer>
            <DeleteButton
              variant="outlined"
              onClick={handleDelete}
              disabled={!entry}
            >
              Delete
            </DeleteButton>
            <SaveButton variant="contained" onClick={handleSave}>
              Save
            </SaveButton>
          </ButtonContainer>
        </DialogContent>
      </StyledDialog>
    </LocalizationProvider>
  );
};
